import React, { useEffect, useState, lazy, Suspense } from 'react';
import "./App.scss";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
    useNavigate
} from "react-router-dom";
import { Box, useMediaQuery } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { RecoilRoot, useRecoilState, useSetRecoilState } from 'recoil';
import { ToastContainer } from 'react-toastify';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { taskInit } from './Api/InitialApi/TaskInitApi';
import { fetchMasterGlFunc } from './Utils/globalfun';
import 'react-toastify/dist/ReactToastify.css';
import LoadingBackdrop from './Utils/Common/LoadingBackdrop';
import SocketListener from './Components/Notification/SocketListener';
import NotificationStack from './Components/Notification/NotificationStack';

import NotificationTable from './Pages/Notification/NotificationTable';
import { userRoleAtom, webReload } from './Recoil/atom';
import { isLoggedInSession, getAuthParams, clearSession } from './Utils/sessionKeys';
import LoginPage from './Components/Auth/LoginForm';
import TemplateDialog from './Components/Common/TemplateDialog';
import TestPage from './Components/Examples/testpage';
import DocumentSheet from './Components/PrintSheet/DocumentSheet';
import MaintenanceSheet from './Components/PrintSheet/MaintenanceSheet';
// import CalendarComparisonDemo from './Backup/CalendarComparisonDemo';
// import CalendarViewDemo from './Backup/CalendarViewDemo';
// import SampleQuickForm from './Backup/sampleQuickForm';
// import SampleQuickFormVersion2 from './Backup/sampleQuickFormVersion2';
// import StructuredQuickTask from './Backup/StructuredQuickTask';
// import DocumentSheet from './Components/PrintSheet/DocumentSheet';
// import AppLayout from './Image_Editor/AppLayout';

// Lazy Components
const Sidebar = lazy(() => import('./Components/NavSidebar/Sidebar'));
const Header = lazy(() => import('./Components/Header/Header'));
const Home = lazy(() => import('./Pages/Home/Home'));
const Inbox = lazy(() => import('./Pages/Inbox/Inbox'));
const Calendar = lazy(() => import('./Pages/Calendar/CalendarPage'));
const CalendarGridView = lazy(() => import('./Pages/Calendar/CalendarGridView'));
const Meeting = lazy(() => import('./Pages/Meeting/Meeting'));
const Task = lazy(() => import('./Pages/Task/Task'));
const TaskTabShell = lazy(() => import('./Pages/Task/TaskTabShell'));
const PersistentTaskShell = lazy(() => import('./Pages/Task/PersistentTaskShell'));
const FullTaskView = lazy(() => import('./Components/Task/FullTaskView/FullTaskView'));
const Project = lazy(() => import('./Pages/Project/Project'));
const Masters = lazy(() => import('./Pages/Master/Masters'));
const TaskDetails = lazy(() => import('./Components/Task/TaskDetails/TaskDetails'));
const PagenotFound = lazy(() => import('./Pages/404Page/PagenotFound'));
const MetaDataSet = lazy(() => import('./Utils/MetaData/MetaDataSet'));
const Profile = lazy(() => import('./Pages/ProfilePage/Profile'));
const UnassignedTaskList = lazy(() => import('./Pages/Task/UnAssignedTask/UnassignedTaskList'));
const ProjectDashboard = lazy(() => import('./Pages/Project/ProjectDashboard'));
const Error401Page = lazy(() => import('./Pages/ErrorPages/Error401Page'));

// report
const PmsReport = lazy(() => import('./Pages/Reports/pmsReport'));
const PmsReport2 = lazy(() => import('./Pages/Reports/pms-report-2'));
const CalendarReport = lazy(() => import('./Pages/Reports/CalendarReport/CalendarReport'));
const ModuleMilestoneReport = lazy(() => import('./Pages/Reports/ModuleMilestoneReport/ModuleMilestoneReport'));
const ImageEditorModal = lazy(() => import('./Image-Editor'));
const DocsEstimateReport = lazy(() => import('./Pages/DocsEstimateReport/DocsEstimateReport'));

const Layout = ({ children, pageDataLoaded }) => {
    const isMobile = useMediaQuery('(max-width:712px)');

    return (
        <Box sx={{ display: 'flex', overflow: "auto" }}>
            <SocketListener />
            <NotificationStack />
            {!pageDataLoaded && <Suspense fallback={<LoadingBackdrop />}><Sidebar /></Suspense>}
            <Box sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                padding: isMobile ? '20px 5px' : '10px 15px',
                position: 'relative',
                width: isMobile ? '97%' : '80%',
                overflow: "auto"
            }}>
                {!pageDataLoaded && <Suspense fallback={<LoadingBackdrop />}><Header /></Suspense>}
                <Suspense fallback={<LoadingBackdrop />}><MetaDataSet /></Suspense>
                {/* Persistent Task Shell: always mounted, CSS-hidden on non-task routes */}
                <PersistentTaskShell />
                {children}
            </Box>
        </Box>
    );
};

const ProtectedRoute = ({ children, pageId, pageData, pageDataLoaded }) => {
    if (pageDataLoaded && pageData?.length != 0) {
        const userPages = pageData?.map((item) => item.id.toString());
        const hasAccess = userPages?.includes(pageId.toString());
        return hasAccess ? children : <Navigate to="/error401" replace />;
    }
};

const AppWrapper = () => {
    const [reload, setReload] = useRecoilState(webReload);
    const [pageData, setPageData] = useState([]);
    const [pageDataLoaded, setPageDataLoaded] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [cookieData, setCookieData] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [masterSynced, setMasterSynced] = useState(false);
    const navigate = useNavigate();
    const setRole = useSetRecoilState(userRoleAtom);

    useEffect(() => {
        let timeout;
        const handleRefetch = () => {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                getQueryParams();
            }, 100);
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                handleRefetch();
            }
        };

        const handleFocus = () => {
            handleRefetch();
        };
        getQueryParams();
        window.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        return () => {
            clearTimeout(timeout);
            window.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    const getQueryParams = () => {
        if (isLoggedInSession()) {
            // This tab's session wins; remember-me seed in localStorage is the fallback
            const authQueryParams = getAuthParams();
            if (authQueryParams) {
                const decodedPayload = JSON.parse(authQueryParams);
                setCookieData(decodedPayload);
                setIsReady(true);
                setPageDataLoaded(true);
                setIsLoggedIn(true);
                return decodedPayload;
            } else {
                // If no stored auth data but isLoggedIn is true, redirect to login
                setIsReady(true);
                setPageDataLoaded(true);
                return navigate('/login', { replace: true });
            }
        }

        // Not logged in: clear stored auth and redirect to login
        clearSession();
        setIsReady(true);
        setPageDataLoaded(true);
        return navigate('/login', { replace: true });
    };

    useEffect(() => {
        const checkAndInit = async () => {
            const taskInitToken = sessionStorage.getItem("taskInit");
            // New session detected when sessionStorage was cleared (tab closed & reopened)
            const isNewSession = !taskInitToken;
            let roleData;
            if (isNewSession) {
                const result = await taskInit();
                if (result?.Data?.rd1) {
                    setPageData(result.Data.rd1);
                    setPageDataLoaded(true);
                }
            } else {
                const pageData = JSON.parse(sessionStorage.getItem("pageAccess"));
                if (pageData) {
                    setPageData(pageData);
                    setPageDataLoaded(true);
                }
            }
            // Keep localStorage intact; only fetch missing master items so UI loads fast
            roleData = await fetchMasterGlFunc(false);
            setRole(roleData?.designation);
        };
        if (cookieData) {
            checkAndInit();
        }
    }, [isReady]);

    const toastStyle = {
        borderRadius: "8px",
        backgroundColor: "#fff",
        fontFamily: '"Public Sans", sans-serif',
        fontWeight: 500,
        fontSize: "15px",
        lineHeight: "19px",
        color: "#444050",
        boxShadow: "rgba(100,100,111,0.2) 0px 7px 29px 0px",
        minWidth: "auto",
        width: "fit-content",
        padding: "0px 6px",
        border: "none",
        borderLeft: "4px solid transparent",
        backgroundImage: "linear-gradient(#fff, #fff), linear-gradient(45deg, #6a11cb, #2575fc)",
        backgroundOrigin: "border-box",
        backgroundClip: "padding-box, border-box",
    };

    if (!isReady || !pageDataLoaded) {
        return <LoadingBackdrop isLoading={true} />;
    }

    return (
        <>
            <ToastContainer
                toastStyle={toastStyle}
                hideProgressBar={true}
                position="bottom-right"
                closeButton={false}
                autoClose={2000}
                limit={3}
                newestOnTop
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><LoadingBackdrop /></Box>}>
                    <Routes>
                        <Route path="/error401" element={<Error401Page />} />
                        <Route path="/test4324" element={<TestPage />} />
                        <Route path="/test" element={<DocumentSheet />} />
                        <Route
                            path="/login"
                            element={
                                isLoggedIn ? <Navigate to="/" replace /> : <LoginPage />
                            }
                        />
                        <Route
                            path="*"
                            element={
                                <Layout>
                                    <Routes>
                                        <Route path="/" element={<Home />} />
                                        <Route path="/projects" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId="-1003"><Project /></ProtectedRoute>} />
                                        <Route path="/projects/Dashboard/*" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId="-1003"><ProjectDashboard /></ProtectedRoute>} />
                                        {/* /tasks route is handled by PersistentTaskShell at Layout level */}
                                        <Route path="/tasks/*" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId="-1002"><React.Fragment /></ProtectedRoute>} />
                                        <Route path="/tasks/unassigned" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId="-1002"><UnassignedTaskList /></ProtectedRoute>} />
                                        <Route path="/taskDetails" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId="-1002"><TaskDetails /></ProtectedRoute>} />
                                        <Route path="/myCalendar" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId="-1006"><Calendar /></ProtectedRoute>} />
                                        <Route path="/meetings" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId="-1005"><Meeting /></ProtectedRoute>} />
                                        <Route path="/inbox" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId="-1004"><Inbox /></ProtectedRoute>} />
                                        <Route path="/masters" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId="-1007"><Masters /></ProtectedRoute>} />
                                        <Route path="/account-profile" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId=""><Profile /></ProtectedRoute>} />
                                        <Route path="/reports/pms" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId="-1008"><PmsReport /></ProtectedRoute>} />
                                        <Route path="/reports/pms-2" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId="-1008"><PmsReport2 /></ProtectedRoute>} />
                                        <Route path="/myTasks" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId="-1002"><FullTaskView /></ProtectedRoute>} />
                                        <Route path="/teamCalReport" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId="-1009"><CalendarReport /></ProtectedRoute>} />
                                        <Route path="/milestoneReport" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId="-1010"><ModuleMilestoneReport /></ProtectedRoute>} />
                                        <Route path="/docs-estimate-report" element={<ProtectedRoute pageData={pageData} pageDataLoaded={pageDataLoaded} pageId="-1033"><DocsEstimateReport /></ProtectedRoute>} />
                                        <Route path="/notification" element={<NotificationTable />} />
                                        <Route path="/taskView" element={<CalendarGridView />} />
                                        <Route path="/image-editor" element={<ImageEditorModal open={true} onClose={() => { }} />} />
                                        <Route path="/template-manager" element={<TemplateDialog open={true} onClose={() => { }} />} />
                                        {/* <Route path="/test" element={<CalendarComparisonDemo />} /> */}
                                        {/* <Route path="/test1" element={<CalendarViewDemo />} /> */}
                                        {/* <Route path="/test2" element={<SampleQuickForm />} /> */}
                                        {/* <Route path="/test3" element={<SampleQuickFormVersion2 />} /> */}
                                        {/* <Route path="/test4321" element={<StructuredQuickTask />} /> */}
                                        {/* <Route path="/test4322" element={<CalendarReport />} /> */}
                                        <Route path="/test4323" element={<MaintenanceSheet />} />
                                        <Route path="*" element={<PagenotFound />} />
                                    </Routes>
                                </Layout>
                            }
                        />
                    </Routes>
                </Suspense>
            </LocalizationProvider>
        </>
    );
};

const appTheme = createTheme({
    palette: {
        primary: {
            main: "#7367f0",
        },
    },
});

const App = () => (
    <RecoilRoot>
        <ThemeProvider theme={appTheme}>
            {/* <Router basename="/itaskweb"> */}
            <Router>
                <AppWrapper />
            </Router>
        </ThemeProvider>
    </RecoilRoot>
);

export default App;
